import { gql, useQuery } from '@apollo/client';
import { getLocaleString } from '@assets/i18n/config';
import {
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { round } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const FETCH_BULK_TRADE_RUN = gql`
  query fetchBulkTradeRun($bulkTradeRunId: ObjectID!) {
    fetchBulkTradeRun(bulkTradeRunId: $bulkTradeRunId) {
      bulkTradeRun {
        id
        bulkMutualFundTrades {
          id
          ticker
          exchange
          type
          currency
          moneyAllocatedCents
        }
      }
    }
  }
`;

export type AccountBulkMutualFundTrade = {
  id: string;
  ticker: string;
  exchange: string;
  type: string;
  currency: string;
  moneyAllocatedCents: number;
};

const UpdateMutualFundTradesTable = ({
  type,
  bulkTradeRunId,
}: {
  type: 'BUY' | 'SELL';
  bulkTradeRunId: string;
}) => {
  const { t } = useTranslation('bulkTrader');
  const symbolAfter = !getLocaleString().startsWith('en');

  const [bulkTrades, setBulkTrades] = useState<AccountBulkMutualFundTrade[]>(
    [],
  );

  const { data, loading } = useQuery(FETCH_BULK_TRADE_RUN, {
    variables: {
      bulkTradeRunId,
    },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (data) {
      setBulkTrades(
        data?.fetchBulkTradeRun?.bulkTradeRun?.bulkMutualFundTrades?.filter(
          (x: any) => x.type === type,
        ),
      );
    }
  }, [data, type]);

  return (
    <Box
      data-testid={`bulk-trader-mutual-fund-trades-table-${type.toLowerCase()}`}
    >
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          data-testid={`bulk-trader-mutual-fund-trades-loading-${type.toLowerCase()}`}
        >
          <CircularProgress sx={{ m: 18 }} />
        </Box>
      ) : !bulkTrades.length ? (
        <></>
      ) : (
        <>
          <Grid container justifyContent='space-between'>
            <Grid item>
              <Typography
                sx={{ p: 2 }}
                variant='h6'
                data-testid={`bulk-trader-mutual-fund-trades-title-${type.toLowerCase()}`}
              >
                {t(`bulkMutualFund${type}Trades`)}
              </Typography>
            </Grid>
          </Grid>
          <Table
            data-testid={`bulk-trader-mutual-fund-trades-table-${type.toLowerCase()}`}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  data-testid={`bulk-trader-mutual-fund-trades-header-security-${type.toLowerCase()}`}
                >
                  <Typography variant='overline'>{t('security')}</Typography>
                </TableCell>

                <TableCell
                  data-testid={`bulk-trader-mutual-fund-trades-header-spacer-${type.toLowerCase()}`}
                ></TableCell>

                <TableCell
                  data-testid={`bulk-trader-mutual-fund-trades-header-amount-${type.toLowerCase()}`}
                >
                  <Typography variant='overline'>{t('amountValue')}</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bulkTrades.map(
                (line: AccountBulkMutualFundTrade, index: number) => (
                  <TableRow
                    key={line.id}
                    data-testid={`bulk-trader-mutual-fund-trades-row-${type.toLowerCase()}-${index}`}
                  >
                    <TableCell
                      data-testid={`bulk-trader-mutual-fund-trades-security-${type.toLowerCase()}-${index}`}
                    >
                      <Typography fontWeight='bold'>{line.ticker}</Typography>
                    </TableCell>

                    <TableCell
                      data-testid={`bulk-trader-mutual-fund-trades-spacer-${type.toLowerCase()}-${index}`}
                    ></TableCell>

                    <TableCell
                      sx={{ display: 'flex', alignItems: 'center' }}
                      data-testid={`bulk-trader-mutual-fund-trades-amount-cell-${type.toLowerCase()}-${index}`}
                    >
                      <TextField
                        value={round(line.moneyAllocatedCents / 100, 6)}
                        sx={{ width: '12em' }}
                        size='small'
                        disabled
                        autoComplete='off'
                        inputMode='decimal'
                        data-testid={`bulk-trader-mutual-fund-trades-amount-input-${type.toLowerCase()}-${index}`}
                        InputProps={{
                          [symbolAfter ? 'endAdornment' : 'startAdornment']: (
                            <InputAdornment
                              position={symbolAfter ? 'end' : 'start'}
                            >
                              {line.currency === 'USD' ? 'US$' : '$'}
                            </InputAdornment>
                          ),
                          inputProps: { min: 0, step: 'any' },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </>
      )}
    </Box>
  );
};

export default UpdateMutualFundTradesTable;
